import { useEffect } from 'react'

import { getIsFetchingOrNotStarted } from '@/reducers/fetch'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'

import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { fetchAllQuarterlyTaxEstimateDetailsIfNeeded } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.slice'
import { selectActiveQuarterlyTaxEstimateDetails } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'
import { selectCurrentAnnualTaxFiling } from '@/features/taxes/annual-taxes/annualTaxFilings.selector'
import {
  getIncompleteAnnualTaxActionItemsForYear,
  getIncompleteEoyActionItems,
  getIncompleteQuarterlyTaxActionItemsForYearAndQuarter,
} from './lib/userActionItems.selectors'
import {
  fetchUserActionItemsIfNeeded,
  FETCH_USER_ACTION_ITEMS_KEY,
} from './lib/userActionItems.slice'

import { TaxActionItemsCard } from './cards/TaxActionItemsCard'
import { EoyActionItemsCard } from './cards/EoyActionItemsCard'
import { QTEActionItemsCard } from './cards/QTEActionItemsCard'
import { ToDosCard } from './cards/ToDosCard'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { FEATURE_FLAG_KEYS } from '@/flags'

export const UserActionItemsCard = () => {
  const dispatch = useAppDispatch()
  // Action items will be sorted/grouped by "Category" by default
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)
  const incompleteEoyActionItems = useReselector(getIncompleteEoyActionItems)

  const currentTaxQuarterDetails = useReselector(
    selectActiveQuarterlyTaxEstimateDetails
  )

  const incompleteAnnualTaxActionItems = useReselector(
    getIncompleteAnnualTaxActionItemsForYear,
    currentTaxYear
  )

  const incompleteQuarterlyTaxActionItems = useReselector(
    getIncompleteQuarterlyTaxActionItemsForYearAndQuarter,
    {
      taxYear: currentTaxQuarterDetails?.taxYear,
      taxQuarter: currentTaxQuarterDetails?.taxQuarter,
    }
  )

  const isFetching = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_USER_ACTION_ITEMS_KEY
  )

  const disable2024AnnualTax = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.disable2024AnnualTax,
    false
  )
  const currentAnnualTaxFiling = useReselector(selectCurrentAnnualTaxFiling)
  const isUserDisqualifiedFromAnnualTaxes = Boolean(
    currentAnnualTaxFiling?.disqualifiedAt
  )

  useEffect(() => {
    /* Always fetch */
    dispatch(fetchUserActionItemsIfNeeded({ forceFetch: true }))
    dispatch(fetchAllQuarterlyTaxEstimateDetailsIfNeeded())
  }, [dispatch])

  // Don't show regular todos if Tax and EOY action lists both exist
  const showRegularTodos =
    incompleteEoyActionItems.length === 0 ||
    incompleteAnnualTaxActionItems.length === 0

  return (
    <>
      {incompleteEoyActionItems.length > 0 && !isFetching && (
        <EoyActionItemsCard
          incompleteEoyActionItems={incompleteEoyActionItems}
          isUrgent
        />
      )}

      {incompleteAnnualTaxActionItems.length > 0 &&
        !isFetching &&
        !isUserDisqualifiedFromAnnualTaxes &&
        !disable2024AnnualTax && (
          <TaxActionItemsCard
            incompleteActionItems={incompleteAnnualTaxActionItems}
            isUrgent={incompleteEoyActionItems.length === 0}
          />
        )}

      {incompleteQuarterlyTaxActionItems.length > 0 && !isFetching && (
        <QTEActionItemsCard
          incompleteActionItems={incompleteQuarterlyTaxActionItems}
          isUrgent={
            incompleteEoyActionItems.length === 0 &&
            incompleteAnnualTaxActionItems.length === 0
          }
        />
      )}

      {showRegularTodos && <ToDosCard isFetching={isFetching} />}
    </>
  )
}
